/**
 * 迁移映射：旧 ID ↔ 新 ID 的唯一权威对照。
 *
 * 保留在生产库而不是 staging 的三个理由：
 *   1. 用户投诉「我以前某条消息不见了」，客服拿旧 ID 查；
 *   2. 发现迁移 bug，定位受影响的记录；
 *   3. 财务对账：旧灵力余额与新余额比对。
 *
 * 唯一键**包含 newModel**：一条旧记录可以拆成多个新实体
 * （MessageRecord 同时映射到 Message 和 BrainRun），因此按 legacyId 查会返回多行。
 * 早期版本漏了 newModel，第二条映射被唯一约束静默跳过，
 * 实测一次失败重跑产生了 169,811 个孤儿行。
 */

import { Alert, Card, Col, Input, Row, Select, Space, Table, Tabs, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { DataTable, useTableParams } from "../../components/DataTable";
import { fmtDateTime, fmtNumber, StatusTag } from "../../components/format";

export function MigrationPage() {
  const [tab, setTab] = useState("lookup");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "lookup", label: "ID 查询", children: <Lookup /> },
        { key: "mappings", label: "映射明细", children: <Mappings /> },
        { key: "stats", label: "批次统计", children: <Stats /> },
      ]}
    />
  );
}

interface MappingRow {
  batchTag: string;
  legacySystem: string;
  legacySchema: string;
  legacyTable: string;
  legacyId: string;
  newModel: string;
  newId: string;
  confidence: string;
  note: string | null;
  createdAt: string | null;
}

function Lookup() {
  const [legacyId, setLegacyId] = useState("");
  const [newId, setNewId] = useState("");
  const [query, setQuery] = useState<{ legacyId?: string; newId?: string } | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ["migrationLookup", query],
    queryFn: () => api.get<MappingRow[]>("/api/v1/admin/migration/lookup", query ?? {}),
    enabled: !!query,
  });

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="双向查询"
        description="客服拿旧 ID 查新 ID，排障拿新 ID 反查旧 ID。同一个 legacyId 返回多行是正确的——一条旧记录可以拆成多个新实体。"
      />
      <Card size="small">
        <Space wrap>
          <Input
            style={{ width: 300 }}
            placeholder="旧 ID（legacyId）"
            value={legacyId}
            onChange={(e) => {
              setLegacyId(e.target.value);
              setNewId("");
            }}
            onPressEnter={() => setQuery({ legacyId })}
          />
          <Typography.Text type="secondary">或</Typography.Text>
          <Input
            style={{ width: 340 }}
            placeholder="新 ID（UUIDv7）"
            value={newId}
            onChange={(e) => {
              setNewId(e.target.value);
              setLegacyId("");
            }}
            onPressEnter={() => setQuery({ newId })}
          />
          <button
            className="ant-btn ant-btn-primary"
            onClick={() => setQuery(legacyId ? { legacyId } : { newId })}
            disabled={!legacyId && !newId}
          >
            查询
          </button>
        </Space>
      </Card>

      {error && <Alert type="error" showIcon message={(error as Error).message} />}

      {query && (
        <Card size="small" title={`结果 ${data?.length ?? 0} 条`} loading={isFetching}>
          <Table<MappingRow>
            size="small"
            rowKey={(r) => `${r.legacySystem}-${r.legacyTable}-${r.legacyId}-${r.newModel}`}
            pagination={false}
            dataSource={data ?? []}
            locale={{ emptyText: "没有匹配的映射" }}
            columns={[
              { title: "旧系统", dataIndex: "legacySystem", width: 140 },
              { title: "旧表", dataIndex: "legacyTable", width: 180 },
              { title: "旧 ID", dataIndex: "legacyId", width: 200 },
              { title: "新模型", dataIndex: "newModel", width: 160 },
              {
                title: "新 ID",
                dataIndex: "newId",
                render: (v: string) => (
                  <Typography.Text copyable className="mono">
                    {v}
                  </Typography.Text>
                ),
              },
              { title: "可信度", dataIndex: "confidence", width: 100, render: (v) => <StatusTag value={v} /> },
              { title: "批次", dataIndex: "batchTag", width: 160 },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

const FILTERS = ["batchTag", "legacySystem", "legacyTable", "newModel", "confidence"];

function Mappings() {
  const { searchParams, setParam } = useTableParams(FILTERS);
  return (
    <DataTable<MappingRow>
      path="/api/v1/admin/migration/mappings"
      filterKeys={FILTERS}
      rowKey={"newId" as never}
      toolbar={
        <Space wrap>
          <Input.Search
            allowClear
            style={{ width: 200 }}
            placeholder="批次标记"
            defaultValue={searchParams.get("batchTag") ?? ""}
            onSearch={(v) => setParam("batchTag", v)}
          />
          <Input.Search
            allowClear
            style={{ width: 200 }}
            placeholder="旧表名"
            defaultValue={searchParams.get("legacyTable") ?? ""}
            onSearch={(v) => setParam("legacyTable", v)}
          />
          <Input.Search
            allowClear
            style={{ width: 200 }}
            placeholder="新模型名"
            defaultValue={searchParams.get("newModel") ?? ""}
            onSearch={(v) => setParam("newModel", v)}
          />
          <Select
            allowClear
            style={{ width: 140 }}
            placeholder="可信度"
            value={searchParams.get("confidence") ?? undefined}
            onChange={(v) => setParam("confidence", v)}
            options={["exact", "high", "medium", "manual"].map((v) => ({ value: v, label: v }))}
          />
        </Space>
      }
      columns={[
        { title: "旧系统", dataIndex: "legacySystem", width: 140 },
        { title: "旧 schema", dataIndex: "legacySchema", width: 130 },
        { title: "旧表", dataIndex: "legacyTable", width: 180 },
        { title: "旧 ID", dataIndex: "legacyId", width: 180 },
        { title: "新模型", dataIndex: "newModel", width: 150 },
        { title: "新 ID", dataIndex: "newId", render: (v: string) => <span className="mono">{v}</span> },
        { title: "可信度", dataIndex: "confidence", width: 100, render: (v) => <StatusTag value={v} /> },
        { title: "批次", dataIndex: "batchTag", width: 160 },
        { title: "时间", dataIndex: "createdAt", width: 170, render: fmtDateTime },
      ]}
    />
  );
}

function Stats() {
  const { data, isLoading } = useQuery({
    queryKey: ["migrationStats"],
    queryFn: () =>
      api.get<{
        total: number;
        byNewModel: { newModel: string; count: number }[];
        byBatch: { batchTag: string; count: number }[];
        byConfidence: { confidence: string; count: number }[];
        bySource: { legacySystem: string; legacyTable: string; count: number }[];
      }>("/api/v1/admin/migration/stats"),
  });

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={12}>
        <Card size="small" title={`按新模型（共 ${fmtNumber(data?.total)} 条映射）`} loading={isLoading}>
          <Table
            size="small"
            rowKey="newModel"
            pagination={false}
            dataSource={data?.byNewModel ?? []}
            columns={[
              { title: "新模型", dataIndex: "newModel" },
              { title: "行数", dataIndex: "count", align: "right", render: fmtNumber },
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card size="small" title="按旧库来源" loading={isLoading}>
          <Table
            size="small"
            rowKey={(r: { legacySystem: string; legacyTable: string }) => `${r.legacySystem}.${r.legacyTable}`}
            pagination={false}
            dataSource={data?.bySource ?? []}
            columns={[
              { title: "旧系统", dataIndex: "legacySystem", width: 150 },
              { title: "旧表", dataIndex: "legacyTable" },
              { title: "行数", dataIndex: "count", align: "right", render: fmtNumber },
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card size="small" title="按批次" loading={isLoading}>
          <Table
            size="small"
            rowKey="batchTag"
            pagination={false}
            dataSource={data?.byBatch ?? []}
            columns={[
              { title: "批次标记", dataIndex: "batchTag" },
              { title: "行数", dataIndex: "count", align: "right", render: fmtNumber },
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card size="small" title="按匹配可信度" loading={isLoading}>
          <Table
            size="small"
            rowKey="confidence"
            pagination={false}
            dataSource={data?.byConfidence ?? []}
            columns={[
              { title: "可信度", dataIndex: "confidence", render: (v) => <StatusTag value={v} /> },
              { title: "行数", dataIndex: "count", align: "right", render: fmtNumber },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );
}
